import { z } from "zod";

import { OrderEntity, OrderServicePorts } from "../../../model";

import { Response } from "~/shared/api";
import { withInvertedProperties } from "~/shared/record";
import { capitalize } from "~/shared/str";

const zService = OrderEntity.zSchema.shape.services.element;

const zSourceFromTo = OrderEntity.zFromToSchema;

type Prefix = Extract<keyof OrderEntity.Schema, "from" | "to">;
type WithPrefix<T extends Record<string, unknown>, P extends string> = {
  [K in keyof T as `${P}${Capitalize<string & K>}`]: T[K];
};

function zFromTo<P extends Prefix>(prefix: P) {
  const shape = {
    ...zSourceFromTo.pick({
      floor: true,
      hasAttic: true,
      hasBasement: true,
    }).shape,
    city: zSourceFromTo.shape.city.nullable(),
    address: zSourceFromTo.shape.address.nullable(),
    postalCode: zSourceFromTo.shape.postalCode.nullable(),
    buildingType: zBuildingTypeFromNumber,
    buildingArea: zSourceFromTo.shape.floorArea,
    roomCount: zSourceFromTo.shape.numberOfRooms,
    hasLift: zSourceFromTo.shape.hasElevator,
  };
  const shapeWithPrefix = Object.fromEntries(
    Object.entries(shape).map(([key, value]) => [`${prefix}${capitalize(key)}`, value]),
  ) as WithPrefix<typeof shape, P>;

  return z.object(shapeWithPrefix);
}

const zStatusAsNumber = z
  .number()
  .int()
  .min(0)
  .max(OrderEntity.zStatusSchema.options.length - 1);

const STATUS_BY_NUMBER = Object.fromEntries(
  [
    OrderEntity.zStatusSchema.Values.NEW,
    OrderEntity.zStatusSchema.Values.IN_PROGRESS,
    OrderEntity.zStatusSchema.Values.ASSIGNED,
    OrderEntity.zStatusSchema.Values.COMPLETED,
    OrderEntity.zStatusSchema.Values.CANCELLED,
  ].map((value, index) => [index, value]),
) as Record<number, OrderEntity.Status>;

const NUMBER_BY_STATUS = withInvertedProperties(STATUS_BY_NUMBER);

const zStatusFromNumber = zStatusAsNumber
  .transform((value) => STATUS_BY_NUMBER[value])
  .pipe(OrderEntity.zStatusSchema);

const zBuildingTypeAsNumber = z
  .number()
  .int()
  .min(0)
  .max(OrderEntity.zBuildingTypeSchema.options.length - 1);

const BUILDING_TYPE_BY_NUMBER = Object.fromEntries(
  [
    OrderEntity.zBuildingTypeSchema.Values.PRIVATE_HOUSE,
    OrderEntity.zBuildingTypeSchema.Values.APARTMENT,
    OrderEntity.zBuildingTypeSchema.Values.OFFICE,
  ].map((value, index) => [index, value]),
) as Record<number, OrderEntity.BuildingTypeSchema>;

const NUMBER_BY_BUILDING_TYPE = withInvertedProperties(BUILDING_TYPE_BY_NUMBER);

const zBuildingTypeFromNumber = zBuildingTypeAsNumber
  .transform((value) => BUILDING_TYPE_BY_NUMBER[value])
  .pipe(OrderEntity.zBuildingTypeSchema);

const zReferralSourceAsNumber = z
  .number()
  .int()
  .min(0)
  .max(OrderEntity.zReferralSourceSchema.options.length - 1);

const REFERRAL_SOURCE_BY_NUMBER = Object.fromEntries(
  [
    OrderEntity.zReferralSourceSchema.Values.WEB_SEARCH,
    OrderEntity.zReferralSourceSchema.Values.FRIENDS_RECOMMENDATIONS,
    OrderEntity.zReferralSourceSchema.Values.SOCIAL_NETWORKS,
    OrderEntity.zReferralSourceSchema.Values.ADVERTISEMENT,
    OrderEntity.zReferralSourceSchema.Values.OTHER,
  ].map((value, index) => [index, value]),
) as Record<number, OrderEntity.ReferralSourceSchema>;

const NUMBER_BY_REFERRAL_SOURCE = withInvertedProperties(REFERRAL_SOURCE_BY_NUMBER);

const zReferralSourceFromNumber = zReferralSourceAsNumber
  .transform((value) => REFERRAL_SOURCE_BY_NUMBER[value])
  .pipe(OrderEntity.zReferralSourceSchema);

const zGenderAsNumber = z
  .number()
  .int()
  .min(0)
  .max(OrderEntity.zGenderSchema.options.length - 1);

const GENDER_BY_NUMBER = Object.fromEntries(
  [
    OrderEntity.zGenderSchema.Values.MALE,
    OrderEntity.zGenderSchema.Values.FEMALE,
    OrderEntity.zGenderSchema.Values.UNDETERMINED,
  ].map((value, index) => [index, value]),
) as Record<number, OrderEntity.GenderSchema>;

const NUMBER_BY_GENDER = withInvertedProperties(GENDER_BY_NUMBER);

const zGenderFromNumber = zGenderAsNumber
  .transform((value) => GENDER_BY_NUMBER[value])
  .pipe(OrderEntity.zGenderSchema);

const zOutSchema = OrderEntity.zSchema
  .pick({
    id: true,
  })
  .extend({
    orderNumber: OrderEntity.zSchema.shape.number,
    ...zFromTo("from").shape,
    ...zFromTo("to").shape,
    whatToTransport: OrderEntity.zSchema.shape.cargoDescription.nullable(),
    infoSource: zReferralSourceFromNumber,
    infoSourceDetails: OrderEntity.zReferralInfoSchema.options[1].shape.details.nullable(),
    contactName: OrderEntity.zSchema.shape.contactPerson.shape.fullName.nullable(),
    contactEmail: OrderEntity.zSchema.shape.contactPerson.shape.email.nullable(),
    contactPhone: OrderEntity.zSchema.shape.contactPerson.shape.phone.nullable(),
    contactGender: zGenderFromNumber,
    status: zStatusFromNumber,
    isPaid: z.boolean(),
    totalCost: z.number().nonnegative(),
    totalPrice: OrderEntity.zSchema.shape.cost,
    services: z.array(
      zService
        .pick({
          price: true,
          quantity: true,
        })
        .extend({
          serviceId: zService.shape.id,
          serviceName: zService.shape.name,
        }),
    ),
    createdDate: z.string().datetime(),
    orderDateTime: z.string().datetime(),
  });
type OutSchema = z.infer<typeof zOutSchema>;

const zBaseOutSchema2 = OrderServicePorts.ProviderIos.Common.zOutSchema2;

const zOutSchema2 = zBaseOutSchema2
  .pick({
    id: true,
  })
  .extend({
    orderNumber: zBaseOutSchema2.shape.number,
    status: zStatusFromNumber,

    totalPrice: OrderEntity.zSchema.shape.cost,
    createdDate: z.string().datetime(),
    orderDateTime: z.string().datetime(),
    contactName: zBaseOutSchema2.shape.contactPersonFullName.nullable(),
    fromAddress: zBaseOutSchema2.shape.from.nullable(),
    toAddress: zBaseOutSchema2.shape.to.nullable(),
  })
  .merge(
    zOutSchema.pick({
      isPaid: true,
      totalCost: true,
    }),
  );
type OutSchema2 = z.infer<typeof zOutSchema2>;

const zRes401 = Response.Schema.zUnsuccessful(401);

export {
  NUMBER_BY_BUILDING_TYPE,
  NUMBER_BY_GENDER,
  NUMBER_BY_REFERRAL_SOURCE,
  NUMBER_BY_STATUS,
  zBuildingTypeFromNumber,
  zGenderFromNumber,
  zOutSchema,
  zOutSchema2,
  zReferralSourceFromNumber,
  zRes401,
  zStatusFromNumber,
};
export type { OutSchema, OutSchema2 };
